import { APIFilterOperator } from '../../../../../../typings/API.interface';
import { IgnoredEventCause } from '../../../../../../typings/IgnoredEvent.interface';

// Base operation types
export type BulkOperationType = 'retry' | 'cancel';

// Supported models for operations
export type BulkOperationModel = 'events' | 'requests' | 'ignored_events';

// Base filters that all bulk operations support
export interface BaseBulkOperationFilters {
  date?: {
    min?: string;
    max?: string;
  };
  source_id?: string | string[];
  destination_id?: string | string[];
  webhook_id?: string | string[];
  cli_user_id?: string | string[];
  body?: string;
  headers?: string;
  path?: string;
  query?: string;
  search_term?: string;
  created_at?: {
    gte?: string;
    lte?: string;
  };
}

// Specific filters for different operation types
export interface BulkRetryFilters extends BaseBulkOperationFilters {
  // Events-specific filters
  status?: string | string[];
  response_status?:
    | {
        gte?: number;
        lte?: number;
      }
    | string[];
  issue_id?: string | string[];
  error_code?: string[];
  next_attempt_at?: string | APIFilterOperator;
  // Requests-specific filters
  rejection_cause?: string[];
  ingested_at?: {
    gte?: string;
    lte?: string;
  };
  // Ignored events filters
  cause?: IgnoredEventCause[];
  transformation_id?: string;
}

export interface BulkCancelFilters extends BaseBulkOperationFilters {
  status?: string[];
  response_status?: {
    gte?: number;
    lte?: number;
  };
  issue_id?: string[];
  error_code?: string[];
  next_attempt_at?: string | APIFilterOperator;
}

// Union type for all possible filters
export type BulkOperationFilters = BulkRetryFilters | BulkCancelFilters;

// Configuration for operation-specific behavior
export interface BulkOperationConfig {
  type: BulkOperationType;
  model?: BulkOperationModel; // Only needed for retry operations
  label: string;
  label_plural: string;
  icon: string;
  button_style: 'outline' | 'danger';
  supports_multiple_models: boolean;
  requires_model_selection: boolean;
  // Validation rules
  invalid_statuses?: string[];
  exclude_from_count_statuses?: string[];
  // API endpoints
  count_api_key: string;
  list_api_key: string;
  plan_api_key: string;
  // Progress calculation
  progress_calculation: 'direct' | 'percentage';
  // Navigation
  navigation_path: string;
  navigation_label: string;
}

// Operation status configuration
export interface BulkOperationStatus {
  in_progress: boolean;
  completed_at?: string;
  cancelled_at?: string;
  completed_count?: number;
  estimated_count?: number;
  progress?: number;
  query?: BulkOperationFilters;
  id: string;
  number: number;
  created_at: string;
  type: BulkOperationType;
}

// Props for individual components
export interface BulkOperationHistoryProps {
  operation_type: BulkOperationType;
  model?: BulkOperationModel;
  filters: object;
  revalidateCounts: () => void;
}

export interface BulkOperationProgressProps {
  operation: BulkOperationStatus;
  operation_type: BulkOperationType;
  model?: BulkOperationModel;
  onCancel?: (operation: BulkOperationStatus) => void;
  mutate?: () => void;
}

export interface BulkOperationBreakdownProps {
  operation_type: BulkOperationType;
  model?: BulkOperationModel;
  query: BulkOperationFilters;
}

export interface SubmitBulkOperationProps {
  operation_type: BulkOperationType;
  model?: BulkOperationModel;
  query: BulkOperationFilters;
  revalidateCounts: () => void;
  onSubmit?: () => void;
}

export interface BulkOperationDropdownProps {
  operation_type: BulkOperationType;
  model?: BulkOperationModel;
  filters: BulkOperationFilters;
  onSubmit?: () => void;
  filter_history?: boolean;
}

// Default configurations for different operation types
export const BULK_OPERATION_CONFIGS: Record<BulkOperationType, BulkOperationConfig> = {
  retry: {
    type: 'retry',
    label: 'Bulk retry',
    label_plural: 'Bulk retries',
    icon: 'retry',
    button_style: 'outline',
    supports_multiple_models: true,
    requires_model_selection: true,
    exclude_from_count_statuses: ['pending'],
    count_api_key: 'bulk',
    list_api_key: 'bulk',
    plan_api_key: 'bulk',
    progress_calculation: 'direct',
    navigation_path: '/events',
    navigation_label: 'events',
  },
  cancel: {
    type: 'cancel',
    label: 'Bulk cancel',
    label_plural: 'Bulk cancels',
    icon: 'cancel',
    button_style: 'danger',
    supports_multiple_models: false,
    requires_model_selection: false,
    invalid_statuses: ['successful', 'discarded'],
    count_api_key: 'bulk.cancelEvents',
    list_api_key: 'bulk.cancelEvents',
    plan_api_key: 'bulk.cancelEvents',
    progress_calculation: 'percentage',
    navigation_path: '/events',
    navigation_label: 'events',
  },
};

// Model-specific labels for retry operations
export const BULK_OPERATION_MODEL_LABELS: Record<BulkOperationModel, string> = {
  events: 'events',
  requests: 'requests',
  ignored_events: 'requests with ignored events',
};

// Model-specific navigation paths
export const BULK_OPERATION_MODEL_NAVIGATION: Record<
  BulkOperationModel,
  { path: string; label: string }
> = {
  events: { path: '/events', label: 'events' },
  requests: { path: '/requests', label: 'requests' },
  ignored_events: { path: '/requests', label: 'requests' },
};
